/**
 * ThoughtProcessPanel Component
 *
 * Displays the thought process that led to an assistant's response.
 * Shows reasoning content and corresponding citations in a fixed-width panel.
 *
 * Features:
 * - Fixed 400px width panel layout
 * - Header with title and close button
 * - Scrollable content area
 * - Citation display at bottom
 * - Responsive design (hidden on mobile)
 */

import React from 'react';
import {
  Box,
  Text,
  Group,
  Stack,
  ScrollArea,
  ActionIcon,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { CitationList } from '../MessageMarkdown';
import type { Citation } from '../MessageMarkdown';
import type { ThoughtProcessData } from '../../../../stores/thoughtProcessStore';
import { ThoughtProcessHeader } from './components/ThoughtProcessHeader';
import { ThoughtProcessContent } from './components/ThoughtProcessContent';
import { ThoughtProcessCitations } from './components/ThoughtProcessCitations';
import classes from './ThoughtProcessPanel.module.css';

// =============================================================================
// COMPONENT INTERFACES
// =============================================================================

export interface ThoughtProcessPanelProps {
  thoughtProcess: ThoughtProcessData;
  onClose: () => void;
  className?: string;
  style?: React.CSSProperties;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ThoughtProcessPanel: React.FC<ThoughtProcessPanelProps> = ({
  thoughtProcess,
  onClose,
  className,
  style,
}) => {
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleCitationClick = (citation: Citation) => {
    // Open URL if available
    if (citation.url) {
      window.open(citation.url, '_blank', 'noopener,noreferrer');
    }
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderHeader = () => (
    <ThoughtProcessHeader
      onClose={onClose}
      className={classes.header}
    />
  );

  const renderContent = () => (
    <ThoughtProcessContent
      content={thoughtProcess.thoughtprocess}
      agent_classification={thoughtProcess.agent_classification}
      data_points={thoughtProcess.datapoints}
      className={classes.content}
    />
  );

  const renderCitations = () => {
    //if (thoughtProcess.citations.length === 0) return null;

    return (
      <ThoughtProcessCitations
        citations={[{id: 'citations', text: 'Citations', url: '', index: 0}]}
        onCitationClick={handleCitationClick}
        className={classes.citations}
      />
    );
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Box
      className={`${classes.panel} ${className || ''}`}
      style={style}
    >
      {/* Header */}
      {renderHeader()}

      {/* Scrollable Content Area */}
      <ScrollArea
        className={classes.scrollArea}
        scrollbarSize={6}
        scrollHideDelay={1500}
      >
        <Stack gap="lg" className={classes.contentStack}>
          {/* Main Content */}
          {renderContent()}

          {/* Citations - left out in phase 1
          {renderCitations()}
          */}
        </Stack>
      </ScrollArea>
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default ThoughtProcessPanel;
